class Wishlist {
  constructor() {
    this.storageKey = 'shopify_wishlist';
    this.items = this.getItems();
    this.init();
  }

  init() {
    this.updateHeaderCount();
    this.bindEvents();
    
    // Update wishlist page if we're on it
    if (document.querySelector('.wishlist-page')) {
      this.renderWishlistPage();
    }
    
    // Update product page buttons
    this.updateProductButtons();
  }

  bindEvents() {
    // Add to wishlist buttons
    document.addEventListener('click', (e) => {
      const addButton = e.target.closest('[data-wishlist-add]');
      if (addButton) {
        e.preventDefault();
        const productId = addButton.dataset.productId;
        const productData = {
          id: productId,
          handle: addButton.dataset.productHandle,
          title: addButton.dataset.productTitle,
          image: addButton.dataset.productImage,
          price: addButton.dataset.productPrice,
          vendor: addButton.dataset.productVendor || '',
          url: addButton.dataset.productUrl
        };
        this.toggleItem(productData);
      }

      // Remove from wishlist
      const removeButton = e.target.closest('[data-wishlist-remove]');
      if (removeButton) {
        e.preventDefault();
        const productId = removeButton.dataset.productId;
        this.removeItem(productId);
      }
    });
  }

  getItems() {
    try {
      const items = localStorage.getItem(this.storageKey);
      return items ? JSON.parse(items) : [];
    } catch (e) {
      console.error('Error reading wishlist:', e);
      return [];
    }
  }

  saveItems() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.items));
    } catch (e) {
      console.error('Error saving wishlist:', e);
    }
  }

  toggleItem(product) {
    const index = this.items.findIndex(item => item.id === product.id);
    
    if (index > -1) {
      this.removeItem(product.id);
    } else {
      this.addItem(product);
    }
  }

  addItem(product) {
    if (!this.items.find(item => item.id === product.id)) {
      this.items.push(product);
      this.saveItems();
      this.updateHeaderCount();
      this.updateProductButtons();
      this.showNotification(`${product.title} added to wishlist`);
      
      if (document.querySelector('.wishlist-page')) {
        this.renderWishlistPage();
      }
    }
  }

  removeItem(productId) {
    this.items = this.items.filter(item => item.id !== productId);
    this.saveItems();
    this.updateHeaderCount();
    this.updateProductButtons();
    
    if (document.querySelector('.wishlist-page')) {
      this.renderWishlistPage();
    }
    
    this.showNotification('Item removed from wishlist');
  }

  isInWishlist(productId) {
    return this.items.some(item => item.id === productId);
  }

  updateHeaderCount() {
    const countBubble = document.querySelector('.wishlist-count-bubble');
    if (countBubble) {
      const count = this.items.length;
      countBubble.querySelector('span[aria-hidden="true"]').textContent = count;
      countBubble.querySelector('.visually-hidden').textContent = `${count} items`;
      countBubble.style.display = count > 0 ? 'flex' : 'none';
      countBubble.dataset.count = count;
    }
  }

  updateProductButtons() {
    document.querySelectorAll('[data-wishlist-add]').forEach(button => {
      const productId = button.dataset.productId;
      const isAdded = this.isInWishlist(productId);
      
      if (isAdded) {
        button.classList.add('added');
        const text = button.querySelector('.wishlist-button-text');
        if (text) text.textContent = 'In Wishlist';
      } else {
        button.classList.remove('added');
        const text = button.querySelector('.wishlist-button-text');
        if (text) text.textContent = 'Add to Wishlist';
      }
    });
  }

  renderWishlistPage() {
    const container = document.querySelector('.wishlist-items-container');
    if (!container) return;

    if (this.items.length === 0) {
      container.innerHTML = `
        <div class="wishlist-empty">
          <div class="wishlist-empty__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </div>
          <h2>Your wishlist is empty</h2>
          <p>Save your favorite items here</p>
          <a href="/collections/all" class="button">Continue Shopping</a>
        </div>
      `;
      return;
    }

    const itemsHTML = this.items.map(item => `
      <div class="wishlist-item" data-product-id="${item.id}">
        <button class="wishlist-item__remove" data-wishlist-remove data-product-id="${item.id}" aria-label="Remove from wishlist">
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"/>
          </svg>
        </button>
        <div class="wishlist-item__image-wrapper">
          <a href="${item.url}">
            <img src="${item.image}" alt="${item.title}" class="wishlist-item__image" loading="lazy">
          </a>
        </div>
        <div class="wishlist-item__info">
          ${item.vendor ? `<div class="wishlist-item__vendor">${item.vendor}</div>` : ''}
          <h3 class="wishlist-item__title">
            <a href="${item.url}">${item.title}</a>
          </h3>
          <div class="wishlist-item__price">${item.price}</div>
          <div class="wishlist-item__actions">
            <button class="wishlist-item__add-to-cart" data-add-to-cart data-product-id="${item.id}">
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    `).join('');

    container.innerHTML = `<div class="wishlist-grid">${itemsHTML}</div>`;
    
    // Bind add to cart events
    this.bindAddToCartEvents();
  }

  bindAddToCartEvents() {
    document.querySelectorAll('[data-add-to-cart]').forEach(button => {
      button.addEventListener('click', async (e) => {
        const productId = button.dataset.productId;
        button.disabled = true;
        button.textContent = 'Adding...';
        
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: productId,
              quantity: 1
            })
          });
          
          if (response.ok) {
            button.textContent = 'Added!';
            // Trigger cart update
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            setTimeout(() => {
              button.disabled = false;
              button.textContent = 'Add to Cart';
            }, 2000);
          } else {
            throw new Error('Failed to add to cart');
          }
        } catch (error) {
          console.error('Error adding to cart:', error);
          button.textContent = 'Try Again';
          button.disabled = false;
        }
      });
    });
  }

  showNotification(message) {
    // Create simple notification
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      bottom: 2rem;
      right: 2rem;
      background: rgb(var(--color-button));
      color: rgb(var(--color-button-text));
      padding: 1.5rem 2rem;
      border-radius: 0.5rem;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      animation: slideIn 0.3s ease;
    `;
    notification.textContent = message;
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }
}

// Add CSS animations
const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
  @keyframes slideOut {
    from { transform: translateX(0); opacity: 1; }
    to { transform: translateX(100%); opacity: 0; }
  }
`;
document.head.appendChild(style);

// Initialize wishlist
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new Wishlist());
} else {
  new Wishlist();
}